import { dailyUnits, type BmnMinedRow, type TokenSupplyRow, type UnitsOutstanding } from 'models/priceChart'
import type { Project } from 'models/project'
import { ProjectTypes } from 'constants/enums'
import { toChartPoints, type ChartPoint } from 'models/chart'
import { latestDate } from './shared'
import type { ProductChart } from './types'

const toNumber = (value: string | number | undefined): number | undefined => {
  const number = Number(value)
  return value === undefined || value === '' || !Number.isFinite(number) ? undefined : number
}

/** A daily `bmnMined` value as a chart series; rows without it are skipped. */
const minedSeries = (rows: BmnMinedRow[], value: (row: BmnMinedRow) => number | undefined): ChartPoint[] =>
  toChartPoints(rows, (row) => row.time, value)

/** A running note (BMN2) still expects BTC; a matured one (BMN1) has nothing left to mine. */
export const isRunningNote = (rows: BmnMinedRow[] | undefined): boolean =>
  (rows ?? []).some(
    (row) => row.remaining_hashprice_in_btc != null && toNumber(row.remaining_hashprice_in_btc) !== undefined,
  )

/** The first day of the note's mined data (`YYYY-MM-DD`): where its token supply is asked from. */
export const firstMinedDay = (rows: BmnMinedRow[] | undefined): string | undefined =>
  (rows ?? [])
    .map((row) => new Date(row.time))
    .filter((date) => Number.isFinite(date.getTime()))
    .sort((a, b) => a.getTime() - b.getTime())[0]
    ?.toISOString()
    .slice(0, 10)

/** The note's issue price: its first tranche's (BMN1: €200,000). */
const nominalPrice = (sales: Project['sales']): number | undefined =>
  [...(sales ?? [])]
    .filter((sale) => sale.startDate && Number(sale.tokenPrice) > 0)
    .sort((a, b) => new Date(a.startDate as string).getTime() - new Date(b.startDate as string).getTime())
    .map((sale) => Number(sale.tokenPrice))[0]

/** A note charted from its supply: notes outstanding × the token price; none without a price. */
const toSupplyIssuanceChart = (
  project: Pick<Project, 'tokenSymbol' | 'tokenPrice'>,
  currency: string,
  units: UnitsOutstanding,
): ProductChart | undefined => {
  const price = Number(project.tokenPrice)
  const supply = dailyUnits(units)
  if (!(price > 0) || !supply.length) return undefined
  const issuance = supply.map(([time, notes]): ChartPoint => [time, notes * price])
  return {
    kind: 'miningNote',
    symbol: project.tokenSymbol?.trim() || '',
    labelKey: 'stoPage.heroChart.issuance',
    denominations: [{ currency, points: [] }],
    note: { issuance, supply },
    asOf: latestDate(issuance),
  }
}

/**
 * Mining note (BMN1, BMN2): while it runs (BMN2) the BTC mined so far per note, in BTC (as its
 * mining dashboard); once matured (BMN1) the same at each day's price in its currency,
 * and while it runs the issuance amount of all notes — (BTC still expected + BTC mined) per note
 * × the price × the notes outstanding that day (as rwa.xyz). Without the supply there is no
 * issuance amount. Once matured (BMN1) it compares with holding the nominal in BTC instead.
 *
 * A note without mining data (PKH2) is charted from its supply alone: its issuance amount is the
 * notes outstanding × the token price, day by day (as rwa.xyz), and nothing mined.
 */
export const toMiningNoteChart = (
  project: Pick<Project, 'type' | 'tokenSymbol' | 'tokenCurrency' | 'tokenPrice' | 'sales'> | undefined,
  rows: BmnMinedRow[] | undefined,
  supply?: TokenSupplyRow[],
  units?: UnitsOutstanding,
): ProductChart | undefined => {
  if (project?.type !== ProjectTypes.TRANCHES) return undefined
  const currency = project.tokenCurrency?.trim() || 'USD'
  if (!rows?.length) return units ? toSupplyIssuanceChart(project, currency, units) : undefined
  const isEuro = currency.toUpperCase() === 'EUR'
  const price = (row: BmnMinedRow) => (isEuro ? row.btc_in_eur : row.btc_in_usd)
  const running = isRunningNote(rows)
  // In BTC while it runs, so the line is the mining alone, not the BTC price; BMN1 compares in money.
  const points = running
    ? minedSeries(rows, (row) => toNumber(row.cumulativeRewards))
    : minedSeries(rows, (row) => (isEuro ? row.cumulative_in_eur : row.cumulative_in_usd))
  // Notes outstanding by UTC day; a day without notes (before the first tranche) has no amount.
  const notesOn = new Map((supply ?? []).map((row) => [row.date, Number(row.tokens)]))
  const issuance = minedSeries(rows ?? [], (row) => {
    const remaining = toNumber(row.remaining_hashprice_in_btc)
    const mined = toNumber(row.cumulativeRewards)
    const btcPrice = price(row)
    const notes = notesOn.get(new Date(row.time).toISOString().slice(0, 10))
    return remaining === undefined || mined === undefined || !btcPrice || !notes || !(notes > 0)
      ? undefined
      : (remaining + mined) * btcPrice * notes
  })
  const minedBtc = [...rows]
    .reverse()
    .map((row) => toNumber(row.cumulativeRewards))
    .find((value) => value !== undefined)
  const prices = minedSeries(rows ?? [], price)
  const nominal = nominalPrice(project.sales)
  const firstPrice = prices[0]?.[1]
  const buyAndHold =
    !running && nominal && firstPrice
      ? { nominal, points: prices.map(([time, value]): ChartPoint => [time, (nominal / firstPrice) * value]) }
      : undefined
  return {
    kind: 'miningNote',
    symbol: project.tokenSymbol?.trim() || '',
    labelKey: running ? 'stoPage.heroChart.minedBtcPerNote' : 'stoPage.heroChart.minedBtc',
    denominations: [{ currency: running ? 'BTC' : currency, points }],
    note:
      issuance.length > 0 || buyAndHold || minedBtc !== undefined
        ? {
            issuance: issuance.length > 0 ? issuance : undefined,
            buyAndHold,
            minedBtc,
            issuanceCurrency: running ? currency : undefined,
          }
        : undefined,
    asOf: latestDate(points),
  }
}

/**
 * A note charted from its supply (PKB2) whose notes are all redeemed: the first day of its final
 * zero. Undefined while notes are outstanding, or for a note never charted from its supply.
 */
export const redeemedOn = (chart: Pick<ProductChart, 'note'> | undefined): number | undefined => {
  const supply = chart?.note?.supply
  if (!supply?.length || supply[supply.length - 1][1] !== 0) return undefined
  let last = supply.length - 1
  while (last > 0 && supply[last - 1][1] === 0) last -= 1
  return last > 0 ? supply[last][0] : undefined
}
